import { TELEPHONE_NUMBER_REGEX } from '@cibo/core'
import { Loading } from '@cibo/profile'
import { UsdaMailingAddressField } from '@cibo/profile/src/features/ResourceDetailFeatures/EQIP/UsdaGrowerDetails/UsdaMailingAddressField'
import { ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, FormLabel, Grid2 as Grid, Stack, Typography } from '@mui/material'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { TextField } from 'formik-mui'
import { equals } from 'ramda'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../../queries'
import { EntityContactInformation, EntityContactInformationDetail } from './types'

type EntityContactFormValues = EntityContactInformation & {
  showAddress: boolean
}

const formValuesFromDetail = (detail: EntityContactInformationDetail) => {
  const { value } = detail

  return {
    ...value,
    showAddress: !!value?.mailingAddress,
  }
}

const detailFromFormValues = (values: EntityContactFormValues) => {
  const { showAddress, ...rest } = values
  return {
    traitId: 'eqipEntity' as const,
    value: {
      ...rest,
      mailingAddress: showAddress ? values.mailingAddress : undefined,
    },
  }
}

export const EntityContactInformationTaskEditorForm = ({
  engagementId,
  detailRequirements,
  onUpdating,
  onError,
  onSuccess,
}: ResourceDetailFeatureTaskEditorProps<EntityContactInformationDetail>) => {
  const { t } = useTranslation('@cibo/programs/EntityContactInformationTaskEditor')

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const updateResourceDetail = useUpdateProgramEngagementDetails<EntityContactInformationDetail>()
  const details = useProgramEngagementDetails({ resourceId: engagementId, detailRequirements })

  const detail: EntityContactInformationDetail = details
    ?.data?.[0] as EntityContactInformationDetail

  const { values, isValid, dirty, isValidating, setFieldValue } =
    useFormikContext<EntityContactFormValues>()

  useEffect(() => {
    if (isValidating) return

    const newDetail = detailFromFormValues(values)

    if (!isValid || equals(detail?.value, newDetail.value) || !dirty) return

    onUpdating?.()
    updateResourceDetail
      .mutateAsync({
        resourceId: engagementId,
        details: [newDetail],
      })
      .then(onSuccess)
      .catch(onError)
  }, [values, isValid, dirty, isValidating])

  const disabled = detail?.immutable

  return (
    <Form>
      <Stack spacing={2}>
        <Stack>
          <Typography variant="h6">{t('aboutEntity')}</Typography>
          <Typography variant="caption">{t('cta')}</Typography>
        </Stack>

        <Stack>
          <FormLabel required>{t('legalName')}</FormLabel>
          <Field
            id="legalName"
            name="legalName"
            component={TextField}
            InputProps={{
              inputProps: {
                'data-testid': 'legalName',
                autoComplete: 'name',
              },
            }}
            maxLength={255}
            placeholder={t('enterLegalName')}
            disabled={disabled}
            fullWidth={true}
          />
        </Stack>

        <Stack>
          <FormLabel required>{t('phoneHelperText')}</FormLabel>
          <Grid container columns={2} rowSpacing={2}>
            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel>{t('mobilePhoneNumber')}</FormLabel>
                <Field
                  id="mobilePhone"
                  name="mobilePhone"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'mobilePhone',
                      autoComplete: 'tel',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterMobilePhoneNumber')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 1 }}>
              <Stack spacing={1} mr={2}>
                <FormLabel>{t('businessPhoneNumber')}</FormLabel>
                <Field
                  id="businessPhone"
                  name="businessPhone"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'businessPhone',
                      autoComplete: 'tel',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('enterBusinessPhoneNumber')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>

        <Stack spacing={1}>
          <FormLabel required={values.showAddress}>{t('entityMailingAddress')}</FormLabel>

          {values.showAddress && (
            <UsdaMailingAddressField name="mailingAddress" disabled={disabled} />
          )}

          <Box>
            {values.showAddress ? (
              <Button startIcon={<CloseIcon />} onClick={() => setFieldValue('showAddress', false)}>
                {t('removeAddress')}
              </Button>
            ) : (
              <Button startIcon={<AddIcon />} onClick={() => setFieldValue('showAddress', true)}>
                {t('addAddress')}
              </Button>
            )}
          </Box>
        </Stack>
      </Stack>
    </Form>
  )
}

const DEFAULT_VALUES = {
  legalName: '',
  mobilePhone: '',
  businessPhone: '',
  showAddress: false,
}

export const EntityContactInformationTaskEditor = (
  props: ResourceDetailFeatureTaskEditorProps<EntityContactInformationDetail>
) => {
  const { t } = useTranslation('@cibo/programs/EntityContactInformationTaskEditor')
  const { detailRequirements, engagementId } = props

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const details = useProgramEngagementDetails<EntityContactInformationDetail>({
    resourceId: engagementId,
    detailRequirements,
  })
  const detail = details?.data?.[0]

  const schema = useMemo(
    () =>
      yup
        .object({
          legalName: yup.string().required(t('legalNameRequired')),
          businessPhone: yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),
          mobilePhone: yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),
        })
        .transform(values => {
          if (values.mailingAddress && Object.values(values).every(v => v === '')) {
            // skip validation if all values are empty
            delete values.mailingAddress
          }
          return values
        })
        .test('phoneNumber', t('phoneRequired'), function (value) {
          const phoneNumberPresent = !!value?.businessPhone || !!value?.mobilePhone

          if (!phoneNumberPresent) {
            return this.createError({
              path: 'mobilePhone',
              message: t('phoneRequired'),
            })
          }

          return true
        }),

    [t]
  )

  if (details.isPending) return <Loading />

  const initialValues = detail ? formValuesFromDetail(detail) : DEFAULT_VALUES

  return (
    <Formik<EntityContactFormValues>
      initialValues={initialValues as EntityContactFormValues}
      onSubmit={() => {}}
      validationSchema={schema}
      validateOnMount
    >
      <EntityContactInformationTaskEditorForm {...props} />
    </Formik>
  )
}
